import createClient from 'openapi-fetch';

import type { AuthResponse } from '../auth';
import type { AppKind } from '../enums';
import { type ErrorCode, isErrorCode } from '../errors';
import type { paths } from './schema';

/**
 * The one API client of the three apps (T4 §2.1): typed calls generated from the OpenAPI document, wrapped
 * with our headers, a single-flight token refresh and timeouts. Screens never write URLs by hand.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode,
    readonly details: Record<string, unknown>,
    readonly requestId: string | null,
  ) {
    super(code);
    this.name = 'ApiError';
  }
}

export class ApiTimeoutError extends Error {
  constructor() {
    super('Request timed out');
    this.name = 'ApiTimeoutError';
  }
}

/** The part of the Web Locks API we use (serializes the refresh across tabs of the same app). */
export interface LockLike {
  request<T>(name: string, callback: () => Promise<T>): Promise<T>;
}

export interface ApiClientOptions {
  /** API origin; '' in the browser (same origin through the /api proxy or Caddy). */
  baseUrl: string;
  app: AppKind;
  appVersion: string;
  getLocale: () => 'fr' | 'ar';
  /** merchant app: the selected shop, sent as X-Merchant-Id */
  getMerchantId?: () => string | null;
  /** a request lost its session (refresh failed): clear state and go to /login with the return URL */
  onAuthLost: () => void;
  /** a new session payload arrived (refresh) */
  onSession?: (session: AuthResponse) => void;
  fetch?: typeof fetch;
  locks?: LockLike;
  /** default 10 s (T4 §2.1) */
  timeoutMs?: number;
}

/** Routes whose 401 means "wrong credentials", never "refresh and retry". */
const NO_REFRESH = /\/auth\/(login|signup|refresh|password\/(forgot|reset))$/;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function errorCodeOf(response: Response): Promise<string | null> {
  try {
    const body = (await response.clone().json()) as { error?: { code?: unknown } };
    return typeof body.error?.code === 'string' ? body.error.code : null;
  } catch {
    return null;
  }
}

export function createApiClient(options: ApiClientOptions) {
  const fetchImpl = options.fetch ?? globalThis.fetch.bind(globalThis);
  const locks =
    options.locks ?? (globalThis as { navigator?: { locks?: LockLike } }).navigator?.locks ?? undefined;
  const timeoutMs = options.timeoutMs ?? 10_000;
  let accessToken: string | null = null;
  let refreshing: Promise<boolean> | null = null;
  let lostNotified: Promise<boolean> | null = null;

  /** One network call with our headers and a timeout. Works on Safari 16.4 (no AbortSignal.any). */
  async function send(request: Request): Promise<Response> {
    const headers = new Headers(request.headers);
    headers.set('x-app', options.app.toLowerCase());
    headers.set('x-app-version', options.appVersion);
    headers.set('accept-language', options.getLocale());
    headers.set('x-request-id', globalThis.crypto.randomUUID());
    const merchantId = options.getMerchantId?.();
    if (merchantId) headers.set('x-merchant-id', merchantId);
    if (accessToken && !headers.has('authorization')) headers.set('authorization', `Bearer ${accessToken}`);

    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);
    const forwardAbort = () => controller.abort();
    request.signal?.addEventListener('abort', forwardAbort);
    try {
      return await fetchImpl(new Request(request, { headers, signal: controller.signal }));
    } catch (error) {
      if (timedOut) throw new ApiTimeoutError();
      throw error;
    } finally {
      clearTimeout(timer);
      request.signal?.removeEventListener('abort', forwardAbort);
    }
  }

  async function doRefresh(attempt = 0): Promise<boolean> {
    const response = await send(
      new Request(`${options.baseUrl}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ app: options.app }),
        credentials: 'include',
      }),
    );
    if (response.ok) {
      const session = (await response.json()) as AuthResponse;
      accessToken = session.accessToken;
      options.onSession?.(session);
      return true;
    }
    // Another tab rotated the cookie a moment ago: its new cookie is already in the jar, ask again once.
    if (attempt === 0 && (await errorCodeOf(response)) === 'CONFLICT_RETRY') {
      await delay(300);
      return doRefresh(1);
    }
    accessToken = null;
    return false;
  }

  /**
   * Single-flight: parallel callers share one refresh; tabs of the same app take turns (Web Locks).
   * Resolves false without side effects, so the boot-time restore of an anonymous visitor stays silent.
   */
  function refresh(): Promise<boolean> {
    refreshing ??= (async () => {
      try {
        const name = `iris-refresh-${options.app.toLowerCase()}`;
        return locks ? await locks.request(name, () => doRefresh()) : await doRefresh();
      } finally {
        refreshing = null;
      }
    })();
    return refreshing;
  }

  /** fetch given to openapi-fetch: on 401 it refreshes once, then replays the original request. */
  async function apiFetch(input: Request | string | URL, init?: RequestInit): Promise<Response> {
    const request = input instanceof Request ? input : new Request(input, init);
    const replay = request.clone();
    const response = await send(request);
    if (response.status !== 401 || NO_REFRESH.test(new URL(request.url).pathname)) return response;
    const code = await errorCodeOf(response);
    if (code !== 'TOKEN_EXPIRED' && code !== 'UNAUTHENTICATED') return response;
    const refreshed = refresh();
    if (!(await refreshed)) {
      // parallel requests share one failed refresh: tell the app once
      if (lostNotified !== refreshed) {
        lostNotified = refreshed;
        options.onAuthLost();
      }
      return response;
    }
    return send(replay);
  }

  const api = createClient<paths>({ baseUrl: options.baseUrl, fetch: apiFetch });

  return {
    api,
    session: {
      getAccessToken: () => accessToken,
      setAccessToken: (token: string | null) => {
        accessToken = token;
      },
      /** Called at boot to turn the refresh cookie into an access token. */
      refresh,
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

/** openapi-fetch result → data, or an ApiError carrying the registry code (non-registry bodies → INTERNAL). */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.response.ok && result.error === undefined) return result.data as T;
  const body = result.error as { error?: { code?: string; details?: Record<string, unknown>; requestId?: string } };
  const code = body?.error?.code;
  if (code && isErrorCode(code)) {
    throw new ApiError(result.response.status, code, body.error?.details ?? {}, body.error?.requestId ?? null);
  }
  throw new ApiError(result.response.status, 'INTERNAL', {}, result.response.headers.get('x-request-id'));
}

/** Earn and validate (T4 §2.1): one automatic retry on a network error or timeout, with the same key. */
export async function withNetworkRetry<T>(work: () => Promise<T>): Promise<T> {
  try {
    return await work();
  } catch (error) {
    if (error instanceof ApiTimeoutError || error instanceof TypeError) return work();
    throw error;
  }
}

/** Created when the confirm screen opens, reused on retry, discarded after success (T4 §2.1). */
export function createIdempotencyKey(): string {
  return globalThis.crypto.randomUUID();
}
